import { api, buildQueryString } from '../core';
import { CASES_TAG } from '../constants';
import { setCasesList, setCasesRowCount } from '@/store';

export interface AllegationDocumentIds {
  allegationDocumentId: string;
  allegationDocumentName: string;
}

export interface AssignedToInfo {
  userId: string;
  userName: string;
}

export interface CreatedByInfo {
  userId: string;
  userName: string;
}

export interface CasesCloneOfInfo {
  caseId: string;
  caseReference: string;
  organisationId: string;
  organisationName: string;
}

export interface CasesItem {
  allegationDocuments: AllegationDocumentIds[];
  assignedTo: AssignedToInfo | null;
  caseId: string;
  caseReference: string;
  createdBy: CreatedByInfo | null;
  dueDate: string | null;
  gRef: string | null;
  isUrgent: boolean;
  paginationProduct: string;
  // Whether the case requires a GDPR check. Hydrates the Create/Edit
  // case sidebar's "Requires GDPR check" checkbox in edit mode.
  runGdpr?: boolean;
  status: string;
  statusUpdated: string;
  clientName: string | null;
  medicalMatchQuestions: unknown[];
  cloneOf: CasesCloneOfInfo | null;
  expiryDate: string | null;
  daysUntilExpiry: number | null;
  dateOfFirstUpload: string | null;
  dateOfCaseClosure: string | null;
  expiryReason: string | null;
  // Optional instructing company id (MLS org only). Hydrates the
  // Create/Edit case sidebar's Instructing-company dropdown in edit mode.
  instructingCompanyId?: string | null;
  // Human-readable name for the currently-assigned instructing company.
  // Lets the sidebar render the saved selection without waiting for —
  // or relying on — list-instructing-companies to include this id.
  instructingCompanyName?: string | null;
}

export const casesItemFieldMap = {
  addedBy: 'added_by',
  assignedTo: 'assigned_to',
  caseId: 'case_id',
  caseReference: 'case_reference',
  status: 'status',
  dueDate: 'due_date',
  statusUpdated: 'status_Updated',
  urgent: 'urgent',
} as const;

export interface CasesResponse {
  cases: CasesItem[];
  numberOfResults: number;
}

type CasesProps = {
  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: string;
  assignedTo?: string[];
  createdBy?: string[];
  status?: string[];
  isUrgent?: boolean;
  caseReference?: string;
};

const casesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCases: builder.query<CasesResponse, CasesProps>({
      query: (params: CasesProps) => {
        const queryString = buildQueryString(params);

        return {
          url: `cases?${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _err, { pageIndex, pageSize, sortBy }) => [
        {
          type: CASES_TAG,
          pageIndex: pageIndex,
          pageSize: pageSize,
          sortBy: sortBy,
        },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data && 'cases' in data && 'numberOfResults' in data) {
            dispatch(setCasesList(data.cases));
            dispatch(setCasesRowCount(data.numberOfResults));
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getCases in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetCasesQuery } = casesApi;
